import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { type Ref, computed, unref } from 'vue'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type { HotelSearchCoverSource } from '../interfaces/recentHotelSearch'

/**
 * La portada de una tarjeta de «Últimas búsquedas»: la ciudad, el país —aunque sólo se
 * haya buscado «México»— o la fachada del hotel.
 *
 * Ya NO se pide a Google desde el navegador: así cada sesión volvía a pagar Places por
 * cada destino. BookingV2 guarda la foto una sola vez —como las fotos de la app móvil— y
 * el historial la manda en `imageUrl`. Sólo una tarjeta que llega sin ella la pide a
 * `GET /api/hotel-searches/{id}/photo`, que la baja, la guarda y regresa su URL; desde
 * ahí, esa foto ya viene en el historial para todos.
 *
 * La llave de la consulta es el destino, no la tarjeta: cinco búsquedas de Monterrey sin
 * foto hacen una sola petición.
 *
 * Si BookingV2 contesta 404 (una búsqueda que quien consulta ya no ve), la tarjeta se
 * queda con el diseño azul: sin `errorHandling: false` el interceptor manda a /error/*.
 */
export default function useDestinationPhoto(
	search: Ref<HotelSearchCoverSource> | HotelSearchCoverSource
) {
	const current = computed(() => unref(search))

	const destinationKey = computed(() => {
		const { placeId, hotelId, destinationType } = current.value

		if (destinationType === 'hotel') return hotelId ? `hotel-${hotelId}` : null

		return placeId ? `place-${placeId}` : null
	})

	const query = useQuery({
		queryKey: ['destination-photo', destinationKey],
		queryFn: async (): Promise<string | null> => {
			const { data } = await axios.get<{ data: { url: string | null } }>(
				`/api/hotel-searches/${current.value.id}/photo`,
				{ errorHandling: false } as CustomAxiosRequestConfig
			)

			return data.data.url
		},
		enabled: computed(() => !current.value.imageUrl && !!destinationKey.value),
		staleTime: Infinity,
		cacheTime: 24 * 60 * 60 * 1000,
		retry: false,
		refetchOnWindowFocus: false,
	})

	const photoUrl = computed(
		() => current.value.imageUrl || query.data.value || null
	)

	return { photoUrl, isLoading: computed(() => query.isFetching.value) }
}
